import { AxiosResponse, Method } from 'axios';
import { AddMultipleNewUserRequest } from 'components/universal/addMultipleUser';
import { AddNewUserRequest } from 'components/universal/addNewUser';
import { DeleteUsersValue } from 'components/universal/deleteUsersForm';
import { EditUserRequest } from 'components/universal/editUserDataForm';
import { sendLoginsAndPasswordsValue } from 'components/universal/generateLoginForm';
import {
    PasswordUpdateDTO,
    ResetPasswordDTO,
    SetNewPasswordDTO,
    SignInDTO,
    SignUpDTO,
} from 'types/user';
import { request } from 'utils/request';
import { UpdateUserRequest } from 'views/complete-profile';
import { Workplace } from 'views/employee-management/useEmployees';
import {
    Department,
    PaginatedResponse,
    WorkplaceType,
} from 'views/registration/useRegistrationHook';

export const signIn = async (userData: SignInDTO) =>
    await request('/auth/login', { method: 'POST' }, userData);

export const confirmRegistration = async (userData: SignInDTO) =>
    await request('/auth/confirm-registration', { method: 'POST' }, userData);
    
export const signUp = async (userData: SignUpDTO) =>
    await request('/auth/registration', { method: 'POST' }, userData);

export const addWorkplace = async (userData: SignUpDTO) =>
    await request('/auth/add-workplace', { method: 'POST' }, userData);

export const logout = async () =>
    await request('/auth/logout/current', { method: 'POST' });

export const passwordUpdate = async (passwordData: PasswordUpdateDTO) =>
    await request(`/auth/password-update`, { method: 'POST' }, passwordData);

export const sendEmailForPasswordReset = async (data: ResetPasswordDTO) =>
    await request(`/auth/password-reset/send-email`, { method: 'POST' }, data);

export const passwordReset = async (data: SetNewPasswordDTO) =>
    await request('/auth/password-reset', { method: 'POST' }, data);

export const getUserData = async () => await request('/user/read', {});

export const getWorkplaces = async ({
    postcode,
    city,
    name,
    page = 1,
}: {
    postcode?: string;
    city?: string;
    name?: string;
    page?: number;
}): Promise<PaginatedResponse<WorkplaceType>> => {
    const params: Record<string, any> = { page };

    if (postcode) params.postcode = postcode;
    if (city) params.city = city;
    if (name) params.name = name;

    const payload = {
        method: 'GET' as Method,
        params,
    };

    const res = await request('/workplaces', payload);
    return res.data;
};

export const getUsers = async ({
    page
}: {
    page: number;
}): Promise<[]> => {
    const params: Record<string, any> = { page };

    const payload = {
        method: 'GET' as Method,
        params,
    };
    const res = await request(`/admin/users`, payload);
    return res.data;
};

export const addNewUser = async (
    payload: AddNewUserRequest
): Promise<AxiosResponse<{ message: string }>> => {
    return await request(`/admin/users`, {
        method: 'POST',
        data: payload.data,
    });
};

export const editUser = async (
    payload: EditUserRequest
): Promise<AxiosResponse<{ message: string }>> => {
    return await request(`/admin/users/${payload.data['userIds']}`, {
        method: 'PUT',
        data: payload.data,
    });
};

export const generateLoginAndPasswordAxsios = async (
    payload: sendLoginsAndPasswordsValue
): Promise<AxiosResponse<Blob>> => {
    return await request(`/admin/credentials`, {
        method: 'POST',
        data: { ids: payload.userIds },
        responseType: 'blob',
    });
};

export const deleteUsersAxios = async (
    payload: DeleteUsersValue
): Promise<AxiosResponse<{ message: string }>> => {
    return await request(`/admin/users`, {
        method: 'DELETE',
        data: { ids: payload.ids },
    });
};

export const getUserWorkplaces = async (): Promise<Workplace[]> => {
    const res = await request(`/admin/workplaces`, { method: 'GET' });
    return res.data.data as Workplace[];
};

export const acceptUserAxios = async (id : number): Promise<
    AxiosResponse<{ message: string }>
> => {
    const res = await request(`/admin/users/${id}/accept`, { method: 'PUT' });
    return res; 
};

export const addMultipleNewUser = async (
    payload: AddMultipleNewUserRequest
): Promise<AxiosResponse<Blob>> => {
    return await request(`/admin/mass-credentials`, {
        method: 'POST',
        data: payload.data,
        responseType: 'blob',
    });
};

export const fetchDepartments = async (workplaceId: number): Promise<Department[]> => {
    const res = await request(`/workplaces/${workplaceId}/departments`, { method: 'GET' });
    return res.data.data as Department[];
};


export const updateUser = async (data: UpdateUserRequest) =>
    await request('/user/update', {
        method: 'POST',
        data,
    });